import React, { useState, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { PlusIcon, ChartBarIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import TaskCard from './TaskCard';
import TaskDetailsModal from '../modals/TaskDetailsModal';
import ConfirmArchiveModal from '../modals/ConfirmArchiveModal';
import ConfirmArchiveTaskModal from '../modals/ConfirmArchiveTaskModal';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { API_ENDPOINTS } from '../../constants/api';
import EmptyState from '../common/EmptyState';
import useDebounce, { useDebouncedCallback } from "../../hooks/useDebounce";
import axiosInstance from "../../api/Axios";
import { TASK_CATEGORIES } from "../../constants/status";

// Функция для перестановки карточек внутри одной категории
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

function DroppableCategory({
  category,
  filteredTasks,
  developers,
  projects,
  handleDeveloperChange,
  handleProjectChange,
  handleHoursChange,
  handleArchiveTask,
  openEditModal,
  openDetailsModal,
  onDelete,
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `column-${category.id}` });
  
  const tasksInCategory = filteredTasks[category.id] || [];
  
  return (
    <SortableContext
      items={tasksInCategory.filter(task => task.id && task.id !== 'undefined' && task.id !== undefined).map((t) => t.id)}
      strategy={verticalListSortingStrategy}
    >
      <div
        ref={setNodeRef}
        className="w-1/2 min-w-[320px] p-4 h-screen overflow-y-auto relative border border-gray-300"
        style={{ background: isOver ? "#f1f5f9" : undefined }}
      >
        <div className="w-full bg-white shadow-md rounded-md mb-4 text-center py-2">
          <h2 className="text-lg font-bold">{category.title}</h2>
        </div>
        <div className="space-y-4 w-full flex flex-col">
          {tasksInCategory.length > 0 ? (
            tasksInCategory.map((item, index) => (
              <TaskCard
                key={item.id || `temp-${index}-${Date.now()}`}
                item={item}
                index={index}
                developers={developers}
                projects={projects}
                handleDeveloperChange={handleDeveloperChange}
                handleProjectChange={handleProjectChange}
                handleHoursChange={handleHoursChange}
                handleArchiveTask={handleArchiveTask}
                openEditModal={openEditModal}
                openDetailsModal={openDetailsModal}
                onDelete={onDelete}
              />
            ))
          ) : (
            <EmptyState title="Нет задач" description="Перетащите сюда задачу" />
          )}
        </div>
      </div>
    </SortableContext>
  );
}

const TaskBoard = ({
  tasks,
  setTasks,
  developers,
  projects,
  categories,
  setSelectedTask,
  fetchTasks,
  openEditModal,
  selectedProject,
  setSelectedProject,
  selectedDeveloper,
  setSelectedDeveloper,
  setIsModalOpen,
  setIsDeveloperSummaryModalOpen,
}) => {
  const [activeTask, setActiveTask] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isArchiveTaskModalOpen, setIsArchiveTaskModalOpen] = useState(false);
  const [taskToArchive, setTaskToArchive] = useState(null);
  const [currentMonth, setCurrentMonth] = useState('');
  const [archiveResult, setArchiveResult] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedTaskForDetails, setSelectedTaskForDetails] = useState(null);
  const { user } = useAuth();

  // Сбрасываем activeTask при монтировании компонента
  useEffect(() => {
    setActiveTask(null);
  }, []);

  // Сбрасываем activeTask при изменении задач
  useEffect(() => {
    if (activeTask && !Object.values(tasks).flat().find(task => task.id === activeTask.id)) {
      setActiveTask(null);
    }
  }, [tasks, activeTask]);

  // API hooks
  const { postData: changeTaskStatus } = useApi(API_ENDPOINTS.UPDATE_TASK);

  // Debounce window resize
  const debouncedWindowWidth = useDebounce(windowWidth, 100);

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
      // Отключаем drag-and-drop для элементов с data-no-dnd
      shouldActivate: (event) => {
        const target = event.target;
        return !target.closest('[data-no-dnd="true"]');
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Слушаем изменение состояния боковой панели через CSS-переменную
  useEffect(() => {
    const handleResize = () => {
      const sidebarWidth = getComputedStyle(document.documentElement)
        .getPropertyValue('--sidebar-width')
        .trim();
      document.documentElement.style.setProperty(
        '--content-width',
        `calc(100vw - ${sidebarWidth})`
      );
      setWindowWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    const observer = new MutationObserver(handleResize);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);

  // Вычисляем ширину контейнера в зависимости от состояния боковой панели
  const getContainerWidth = () => {
    if (debouncedWindowWidth < 768) {
      return 'w-full';
    } else if (debouncedWindowWidth < 1024) {
      return 'w-full';
    } else {
      return 'w-[calc(100vw-80px)]';
    }
  };

  const updatePositionsInDB = useCallback(async (newState) => {
    try {
      const positions = [];
      categories.forEach((category) => {
        const categoryTasks = newState[category.id] || [];
        categoryTasks.forEach((task, index) => {
          // Skip tasks with invalid IDs
          if (!task.id || task.id === 'undefined' || task.id === undefined) {
            console.warn(`Skipping task with invalid ID:`, task);
            return;
          }
          positions.push({
            taskId: task.id,
            droppableId: category.id,
            position: index,
          });
        });
      });

      // Only send request if we have valid positions
      if (positions.length > 0) {
        await axiosInstance.post(API_ENDPOINTS.UPDATE_POSITIONS, positions);
      }
    } catch (error) {
      console.error("Error updating positions:", error);
    }
  }, [categories]);

  // Create a debounced function for position updates
  const debouncedUpdatePositions = useDebouncedCallback(updatePositionsInDB, 500);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    console.log("TaskBoard - handleDragEnd: active:", active, "over:", over);

    if (!over || !active) {
      console.log("TaskBoard - handleDragEnd: no over or active, clearing activeTask");
      setActiveTask(null);
      return;
    }

    // Получаем filteredTasks для каждой категории (только display === true)
    const filteredTasks = {};
    categories.forEach(cat => {
      filteredTasks[cat.id] = tasks[cat.id]?.filter(task => task.display) || [];
    });

    // Найти категорию и индекс задачи ТОЛЬКО среди видимых (filtered)
    let sourceCategory, destCategory, sourceIndex, destIndex;
    
    for (const cat of categories) {
      const idx = filteredTasks[cat.id]?.findIndex((t) => t.id === active.id);
      if (idx !== -1 && idx !== undefined) {
        sourceCategory = cat.id;
        sourceIndex = idx;
        break;
      }
    }
    
    for (const cat of categories) {
      if (over.id === `column-${cat.id}`) {
        destCategory = cat.id;
        destIndex = 0; // вставить в конец
        break;
      }
      const idx = filteredTasks[cat.id]?.findIndex((t) => t.id === over.id);
      if (idx !== -1 && idx !== undefined) {
        destCategory = cat.id;
        destIndex = idx;
        break;
      }
    }
    
    if (!sourceCategory || !destCategory) {
      setActiveTask(null);
      return;
    }

    // Обновляем порядок только среди видимых задач, остальные не трогаем
    let newState = { ...tasks };
    
    if (sourceCategory === destCategory) {
      // Получаем только видимые задачи
      const visible = filteredTasks[sourceCategory];
      // Меняем порядок среди видимых
      const newVisible = reorder(visible, sourceIndex, destIndex);
      // Воссоздаём полный массив: заменяем только видимые задачи, остальные оставляем на месте
      const full = [...tasks[sourceCategory]];
      let vi = 0;
      for (let i = 0; i < full.length; i++) {
        if (full[i].display) {
          full[i] = newVisible[vi++];
        }
      }
      newState[sourceCategory] = full;
    } else {
      // Перемещение между категориями (только среди видимых)
      const sourceFull = [...tasks[sourceCategory]];
      const destFull = [...tasks[destCategory]];
      const visibleSource = filteredTasks[sourceCategory];
      const movedTask = visibleSource[sourceIndex];
      
      // Удалить из sourceFull (по id)
      const srcIdx = sourceFull.findIndex(t => t.id === movedTask.id);
      sourceFull.splice(srcIdx, 1);
      
      // Вставить в destFull на нужную позицию среди видимых
      let insertIdx = destFull.length; // по умолчанию — в конец
      if (filteredTasks[destCategory].length > 0) {
        // Считаем индекс среди всех задач destFull, куда вставить
        let vCount = 0;
        for (let i = 0; i < destFull.length; i++) {
          if (destFull[i].display) {
            if (vCount === destIndex) {
              insertIdx = i;
              break;
            }
            vCount++;
          }
        }
      }
      
      movedTask.category = destCategory;
      
      // Если задача перемещается в статус "завершено" (ITEMS3), устанавливаем дату завершения
      if (destCategory === TASK_CATEGORIES.ITEMS3 && !movedTask.completedAt) {
        movedTask.completedAt = new Date().toISOString();
        
        // Отправляем запрос на сервер для обновления даты завершения
        try {
          await axiosInstance.put(`/edittask/${movedTask.id}`, {
            completedAt: movedTask.completedAt
          });
        } catch (error) {
          console.error("Error updating completion date:", error);
        }
      }
      
      destFull.splice(insertIdx, 0, movedTask);
      newState[sourceCategory] = sourceFull;
      newState[destCategory] = destFull;
    }
    
    setTasks(newState);
    console.log("TaskBoard - handleDragEnd: clearing activeTask");
    setActiveTask(null);
    
    // Trigger debounced position update
    debouncedUpdatePositions(newState);
  };

  const handleDragStart = (event) => {
    const { active } = event;
    console.log("TaskBoard - handleDragStart: active.id:", active.id);
    const activeTaskData = Object.values(tasks)
      .flat()
      .find((task) => task.id === active.id);
    console.log("TaskBoard - handleDragStart: activeTaskData:", activeTaskData);
    setActiveTask(activeTaskData);
  };

  const handleDeveloperChange = async (taskId, developerId) => {
    try {
      await changeTaskStatus({ taskId, developer: developerId });
      await fetchTasks();
    } catch (error) {
      console.error("Error changing developer:", error);
    }
  };

  const handleProjectChange = async (taskId, projectId) => {
    try {
      await changeTaskStatus({ taskId, project: projectId });
      await fetchTasks();
    } catch (error) {
      console.error("Error changing project:", error);
    }
  };

  const handleHoursChange = async (taskId, hours) => {
    try {
      await changeTaskStatus({ taskId, hours });
      await fetchTasks();
    } catch (error) {
      console.error("Error changing hours:", error);
    }
  };

  const handleArchiveTask = (taskId) => {
    // Находим задачу для архивирования
    const task = Object.values(tasks).flat().find(task => task.id === taskId);
    if (task) {
      setTaskToArchive(task);
      setIsArchiveTaskModalOpen(true);
    }
  };

  const handleConfirmArchiveTask = async () => {
    if (!taskToArchive) return;
    
    try {
      await axiosInstance.post(`${API_ENDPOINTS.ARCHIVE_TASK}/${taskToArchive.id}`);
      await fetchTasks();
      setIsArchiveTaskModalOpen(false);
      setTaskToArchive(null);
    } catch (error) {
      console.error("Error archiving task:", error);
    }
  };

  const handleCloseArchiveTaskModal = () => {
    setIsArchiveTaskModalOpen(false);
    setTaskToArchive(null);
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axiosInstance.delete(`${API_ENDPOINTS.DELETE_COMPLETED_TASK}/${taskId}`);
      await fetchTasks();
    } catch (error) {
      console.error("Error deleting task:", error);
    }
  };

  const handleArchiveCompletedMonth = async () => {
    const month = new Date().toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
    setCurrentMonth(month);
    setArchiveResult(null);
    setIsArchiveModalOpen(true);
  };

  const handleConfirmArchive = async () => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.ARCHIVE_COMPLETED_MONTH);
      await fetchTasks();
      setArchiveResult(response.data.message);
    } catch (error) {
      setArchiveResult(error.response?.data?.descr || 'Ошибка при архивации задач');
    }
  };

  const handleCloseArchiveModal = () => {
    setIsArchiveModalOpen(false);
    setArchiveResult(null);
  };

  const openDetailsModal = (task) => {
    setSelectedTaskForDetails(task);
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedTaskForDetails(null);
  };

  return (
    <div className={`${getContainerWidth()} flex flex-col h-full`}>
      {/* Controls */}
      <div className="bg-white shadow-sm border-b border-gray-200 p-4">
        <div className="flex flex-col md:flex-row justify-between space-y-2 md:space-y-0 md:space-x-4">
          <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-4">
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {projects.map((proj) => (
                <option key={proj.id} value={proj.id}>
                  {proj.name}
                </option>
              ))}
            </select>
            <select
              value={selectedDeveloper}
              onChange={(e) => setSelectedDeveloper(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Все разработчики</option>
              {developers
                .filter((dev) => dev.id !== "all")
                .map((dev) => (
                  <option key={dev.id} value={dev.id}>
                    {dev.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="flex space-x-2">
            {user?.roles?.includes('ADMIN') && (
              <button
                onClick={handleArchiveCompletedMonth}
                className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors duration-200 text-sm"
                title="Архивировать все выполненные задачи за текущий месяц"
              >
                <ArchiveBoxIcon className="w-4 h-4 mr-2" />
                Архивировать за месяц
              </button>
            )}
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 text-sm"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Добавить задачу
            </button>
            <button
              onClick={() => setIsDeveloperSummaryModalOpen(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200 text-sm"
            >
              <ChartBarIcon className="w-4 h-4 mr-2" />
              Сводка
            </button>
          </div>
        </div>
      </div>

      {/* Task Board */}
      <div className="flex-1 overflow-x-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex">
            {categories.map((category) => {
              // Получаем отфильтрованные задачи для этой категории
              const filteredTasksForCategory = tasks[category.id]?.filter(task => task.display) || [];
              
              return (
                <DroppableCategory
                  key={category.id}
                  category={category}
                  filteredTasks={{ [category.id]: filteredTasksForCategory }}
                  developers={developers}
                  projects={projects}
                  handleDeveloperChange={handleDeveloperChange}
                  handleProjectChange={handleProjectChange}
                  handleHoursChange={handleHoursChange}
                  handleArchiveTask={handleArchiveTask}
                  openEditModal={openEditModal}
                  openDetailsModal={openDetailsModal}
                  onDelete={handleDeleteTask}
                />
              );
            })}
          </div>
          <DragOverlay>
            {activeTask ? (
              <TaskCard
                item={activeTask}
                developers={developers}
                projects={projects}
                handleDeveloperChange={handleDeveloperChange}
                handleProjectChange={handleProjectChange}
                handleHoursChange={handleHoursChange}
                handleArchiveTask={handleArchiveTask}
                openEditModal={openEditModal}
                onDelete={handleDeleteTask}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Модальное окно подтверждения архивации */}
      <ConfirmArchiveModal
        isOpen={isArchiveModalOpen}
        onClose={handleCloseArchiveModal}
        onConfirm={handleConfirmArchive}
        currentMonth={currentMonth}
        archiveResult={archiveResult}
      />

      {/* Модальное окно деталей задачи */}
      {isDetailsModalOpen && (
        <TaskDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={closeDetailsModal}
          task={selectedTaskForDetails}
          developers={developers}
          projects={projects}
        />
      )}

      {/* Модальное окно подтверждения архивации задачи */}
      <ConfirmArchiveTaskModal
        isOpen={isArchiveTaskModalOpen}
        onClose={handleCloseArchiveTaskModal}
        onConfirm={handleConfirmArchiveTask}
        task={taskToArchive}
        developers={developers}
      />
    </div>
  );
};

export default TaskBoard; 